import { camelCase } from 'lodash';

import { AvatarPerson } from '../../../core/components/Avatar.tsx';
import { url } from '../../../core/utils/link.ts';
import { VOICE_AGENT_CONFIG_PATH, VOICE_AGENT_PATH } from '../../voice_agents/constants.ts';
import { formatSkillKey, formatToolProviderKey } from '../components/config/AgentCardContent.tsx';
import { ASSISTANT_PATH } from '../constants.ts';

export type AssistantKind = 'inboxAgent' | 'voiceAgent';

interface AssistantTeam {
    name: string;
    isDefault: boolean;
}

const resolveTeamName = (team: AssistantTeam | undefined, defaultTeamLabel: string): string | undefined =>
    team && (!team.isDefault ? team.name : defaultTeamLabel);

export interface AssistantCardData {
    id: string;
    kind: AssistantKind;
    name: string;
    subtitle?: string;
    enabled: boolean;
    chips: string[];
    dataSourceLabel?: string;
    phoneLabel?: string;
    testLink?: string;
    mailboxes: string[];
    mailboxOwners: AvatarPerson[];
    detailPath: string;
    configPath: string;
}

export const adaptAgent = (
    agent: any,
    t: (key: string, options?: any) => string,
    masterDataLabel: string,
    defaultTeamLabel: string,
    personalMailboxOwners: Map<string, AvatarPerson>
): AssistantCardData => ({
    id: agent.id,
    kind: 'inboxAgent',
    name: agent.name,
    subtitle: resolveTeamName(agent.team, defaultTeamLabel),
    enabled: agent.enabled,
    chips: (agent.skills || []).map((skill: any) => formatSkillKey(skill.skillKey, t)),
    dataSourceLabel: [
        agent.erpToolProvider?.key && formatToolProviderKey(agent.erpToolProvider.key, t),
        agent.masterDataToolProvider?.channelId && masterDataLabel,
    ]
        .filter(Boolean)
        .join(' · '),
    mailboxes: (agent.smartInboxIntegrations || [])
        .filter((integration: any) => !personalMailboxOwners.has(integration.id))
        .map((integration: any) => integration.mailInbox),
    mailboxOwners: (agent.smartInboxIntegrations || [])
        .map((integration: any) => personalMailboxOwners.get(integration.id))
        .filter(Boolean),
    detailPath: url(ASSISTANT_PATH, { channelId: agent.id }),
    configPath: url(ASSISTANT_PATH, { channelId: agent.id }),
});

// t must be bound to the 'voice_agents' namespace (tools.*.name lives there)
export const adaptVoiceAgent = (
    voiceAgent: any,
    t: (key: string, options?: any) => string,
    defaultTeamLabel: string
): AssistantCardData => ({
    id: voiceAgent.id,
    kind: 'voiceAgent',
    name: voiceAgent.name,
    subtitle: resolveTeamName(voiceAgent.team, defaultTeamLabel),
    enabled: voiceAgent.enabled,
    chips: (voiceAgent.enabledTools || []).map((tool: string) =>
        t(`tools.${camelCase(tool)}.name`, { defaultValue: tool })
    ),
    dataSourceLabel: voiceAgent.erpToolProviderKey ? formatToolProviderKey(voiceAgent.erpToolProviderKey, t) : '',
    phoneLabel: [voiceAgent.language?.toUpperCase(), voiceAgent.phoneNumber].filter(Boolean).join(' · '),
    testLink: voiceAgent.agentLink || undefined,
    mailboxes: [],
    mailboxOwners: [],
    detailPath: url(VOICE_AGENT_PATH, { channelId: voiceAgent.id }),
    configPath: url(VOICE_AGENT_CONFIG_PATH, { channelId: voiceAgent.id, tab: 'details' }),
});
